import { useCallback, useState } from 'react';
import { Image, View, useWindowDimensions } from 'react-native';

import { Button, IconInfo, Text, createMqStyles, tokens, useMqSelect } from '@fhs/ui';

import { StepsProgress } from '../steps-progress';

export interface ChallengeCardProps {
  image: string;
  title: string;
  description: string;
  onPress: () => Promise<void>;
  onPressInfo: () => void;
  buttonText?: string;
  badge?: string;
  progress?: {
    current: number;
    total: number;
    type?: 'percentage' | 'steps';
  } | null;
}

export function ChallengeCard({
  image,
  title,
  description,
  onPress,
  onPressInfo,
  buttonText,
  badge,
  progress,
}: ChallengeCardProps) {
  const styles = useMqStyles();
  const [isLoading, setIsLoading] = useState(false);
  const itemWidth = useChallengeCardWidth();

  const handleOnPress = useCallback(async () => {
    try {
      setIsLoading(true);
      onPress && (await onPress());
    } finally {
      setIsLoading(false);
    }
  }, [onPress]);

  return (
    <View style={[{ width: itemWidth }, styles.card]}>
      <Image source={{ uri: image }} style={styles.image} />
      <View style={styles.content}>
        {badge && (
          <View style={styles.badge}>
            <Text.Ui size="sm" weight="semibold" style={{ color: tokens.colors.$white }}>
              {badge}
            </Text.Ui>
          </View>
        )}
        <Text.Heading type="four">{title}</Text.Heading>
        <Text.Ui size="sm" style={{ marginTop: 8 }}>
          {description}
        </Text.Ui>
        {progress && (
          <StepsProgress
            style={{ marginTop: 8 }}
            currentStep={progress.current}
            totalSteps={progress.total}
          />
        )}
        {buttonText && (
          <Button loading={isLoading} size="xs" onPress={handleOnPress} style={{ marginTop: 16 }}>
            <Button.Text>{buttonText}</Button.Text>
          </Button>
        )}
        <Button type="ghost" style={styles.infoButton} onPress={onPressInfo}>
          <Button.Icon>
            <IconInfo />
          </Button.Icon>
        </Button>
      </View>
    </View>
  );
}

const useMqStyles = createMqStyles({
  card: {
    $base: {
      borderColor: tokens.colors.$blackOpacity10,
      borderWidth: 1,
      borderRadius: 8,
      overflow: 'hidden',
    },
    $gteDesktop: {
      flexDirection: 'row',
      height: 253,
    },
  },
  image: {
    $base: {
      aspectRatio: 2,
    },
    $ltDesktop: {
      width: '100%',
    },
    $gteDesktop: {
      height: '100%',
    },
  },
  content: {
    $gteDesktop: {
      flex: 1,
      padding: 32,
      borderLeftWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      justifyContent: 'center',
    },
    $ltDesktop: {
      padding: 12,
      borderTopWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
    },
  },
  badge: {
    $base: {
      backgroundColor: tokens.colors.$success,
      marginBottom: 12,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 4,
      alignSelf: 'baseline',
    },
    $gteDesktop: {
      marginBottom: 8,
      paddingVertical: 6,
    },
  },
  infoButton: {
    $base: {
      position: 'absolute',
      top: 11,
      right: 12,
      margin: -8,
    },
  },
});

export function useChallengeCardWidth() {
  const { width } = useWindowDimensions();
  const itemWidth = useMqSelect({ $gteDesktop: 908 }, width - 32);

  return itemWidth;
}
